import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Accordion, AccordionTab } from "primereact/accordion";
import { degreesApi } from "../../services/nomenclatures/degreesApi";
import { SelectButton } from "primereact/selectbutton";
import CustomInputText from "../../components/forms/CustomInputText";
import { FormikProvider, useFormik } from "formik";
import { studentsApi } from "../../services/students/studentsApi";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { RadioButton } from "primereact/radiobutton";
import { specialityFeesApi } from "../../services/nomenclatures/specialityFeesApi";
import { RankingApplicationResponse, SpecialityResponse } from "../../models/dtos";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { facultyNumberTemplate } from "../../components/common/FacultyNumberTemplate";
import Error from "../../components/common/Error";
import { specialitiesApi } from "../../services/nomenclatures/specialitiesApi";

const Enrollment = () => {
  const toast = useRef<Toast>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();

  const { data: degrees } = degreesApi.useGetDegreesQuery(true);
  const [getEnrollmentApplication, { data: application, isLoading: isApplicationLoading }] =
    studentsApi.useGetEnrollmentApplicationMutation();
  const [getSpecialityFeeTrigger, specialityFee] = specialityFeesApi.useLazyGetStudentSpecialityFeeQuery();
  const [getSpecialityChildrenTrigger, specialityChildren] = specialitiesApi.useLazyGetSpecialityChildrenQuery();
  const [
    enroll,
    { data: enrollmentResponse, isLoading: isEnrolling, isError: isEnrollmentError, error: enrollmentResponseError },
  ] = studentsApi.useEnrollMutation();

  const formik = useFormik({
    initialValues: {
      degreeId: 0,
      applicationNumber: "",
      applicationId: null,
      rankingApplicationId: null,
      rankingApplication: null as RankingApplicationResponse | null,
      selectedSpecialityId: null,
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      enroll({
        applicationId: values.applicationId!,
        rankingApplicationId: values.rankingApplicationId!,
        specialityId: values.rankingApplication?.specialityId!,
        selectedSpecialityId: values.selectedSpecialityId,
      });
    },
  });

  const handleFirstStep = () => {
    getEnrollmentApplication({
      degreeId: formik.values.degreeId,
      applicationNumber: formik.values.applicationNumber,
    });
    setActiveIndex(1);
  };

  useEffect(() => {
    if (enrollmentResponse?.data && enrollmentResponse?.data.studentId) {
      if (enrollmentResponse?.message) {
        toast.current?.show({ severity: "info", summary: "Успешно записване", detail: enrollmentResponse.message });
      }
      navigate(`/students/${enrollmentResponse.data.studentId}`);
    }
  }, [enrollmentResponse]);

  useEffect(() => {
    if (application?.data) {
      formik.setFieldValue("applicationId", application.data.applicationId);

      if (!formik.values.rankingApplicationId) {
        const selectedApp = application.data.rankingApplications.find((x) => x.isSelected);
        if (selectedApp) {
          formik.setFieldValue("rankingApplicationId", selectedApp.rankingApplicationId);
          formik.setFieldValue("rankingApplication", selectedApp);
          setSelectedRankingApplication(selectedApp);
        }
      }
    }
  }, [application]);

  useEffect(() => {
    if (specialityFee?.data?.data?.specialityId) {
      getSpecialityChildrenTrigger(specialityFee.data.data.specialityId);
    }
  }, [specialityFee?.data?.data?.specialityId]);

  const [selectedRankingApplication, setSelectedRankingApplication] = useState<RankingApplicationResponse | null>(null);

  if (isEnrolling) return <ProgressSpinner />;

  return (
    <div>
      <h1 className="mb-4 mt-0 text-4xl font-light">Записване на студент</h1>
      {isEnrollmentError && <Message severity="error" text="Възникна грешка при записването на студента." />}
      <Error error={enrollmentResponseError} />

      <FormikProvider value={formik}>
        <Accordion activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index as number)}>
          <AccordionTab header="Избор на кандидат-студент">
            <div className="flex flex-column">
              <div className="border-2 border-dashed surface-border border-round surface-ground flex flex-column gap-4 justify-content-start w-full p-4">
                <div className="w-full">
                  <label htmlFor="degreeId">Кандидатстудентска кампания</label>
                  <SelectButton
                    id="degreeId"
                    value={formik.values.degreeId}
                    options={degrees?.data || []}
                    optionLabel="name"
                    optionValue="degreeId"
                    onChange={formik.handleChange}
                    className="mt-2"
                  />
                </div>
                <div className="w-4">
                  <CustomInputText
                    id="applicationNumber"
                    label="Входящ номер"
                    placeholder="Въведете входящ номер"
                    onPressEnter={handleFirstStep}
                  />
                </div>
              </div>
            </div>
            <div className="flex mt-2">
              <Button label="Напред" icon="pi pi-arrow-right" iconPos="right" type="button" onClick={handleFirstStep} />
            </div>
          </AccordionTab>
          <AccordionTab header="Избор на класиране">
            <div className="flex flex-column">
              <div className="border-2 border-dashed surface-border border-round surface-ground flex flex-column gap-4 justify-content-start w-full p-4">
                {isApplicationLoading && <ProgressSpinner />}
                {!application && !isApplicationLoading && (
                  <div className="w-full">
                    <Message severity="error" text="Не бяха намерени данни за въведения входящ номер." />
                  </div>
                )}

                {application && application.data && !isApplicationLoading && (
                  <>
                    <div className="w-6 flex flex-row justify-content-start gap-2">
                      <label className="w-3 text-right">Входящ номер:</label>
                      <label className="flex-1">
                        {application.data.applicationNumber}
                        <a
                          href={`https://priem.uni-sz.bg/operators/impersonate/${application.data.applicationId}`}
                          target="_blank"
                          className="text-blue-500 hover:text-blue-700 ml-2"
                        >
                          <i className="pi pi-external-link"></i>
                        </a>
                      </label>
                    </div>
                    <div className="w-6 flex flex-row justify-content-start gap-2">
                      <label className="w-3 text-right">Име:</label>
                      <label className="flex-1">
                        {application.data.user?.firstName} {application.data.user?.middleName}{" "}
                        {application.data.user?.lastName}
                      </label>
                    </div>
                    <div className="w-6 flex flex-row justify-content-start gap-2">
                      <label className="w-3 text-right">ЕГН:</label>
                      <label className="flex-1">{application.data.user?.egn}</label>
                    </div>

                    {application.data.rankingApplications && application.data.rankingApplications.length > 0 && (
                      <DataTable
                        value={application.data.rankingApplications}
                        emptyMessage="Не бяха намерени класирания."
                        header="Класирания - моля, изберете едно от класиранията"
                        selectionMode="single"
                        selection={selectedRankingApplication}
                        onSelectionChange={(e) => {
                          const selectedApp = e.value as RankingApplicationResponse | null;
                          setSelectedRankingApplication(selectedApp);
                          if (selectedApp) {
                            formik.setFieldValue("rankingApplication", selectedApp);
                            formik.setFieldValue("rankingApplicationId", selectedApp.rankingApplicationId);
                          }
                        }}
                        dataKey="rankingApplicationId"
                      >
                        <Column selectionMode="single" headerStyle={{ width: "3rem" }} />
                        <Column field="rankingName" header="Класиране"></Column>
                        <Column field="specialityName" header="Специалност"></Column>
                        <Column field="speciality.specialityModeName" header="Форма на обучение"></Column>
                        <Column field="statusName" header="Статус"></Column>
                      </DataTable>
                    )}

                    <DataTable
                      value={application.data.existingStudents || []}
                      emptyMessage="Няма съществуващи студенти със същия идентификатор."
                      header="Студенти със същия идентификатор"
                    >
                      <Column field="facultyNumber" header="Факултетен номер" body={facultyNumberTemplate}></Column>
                      <Column field="firstName" header="Име"></Column>
                      <Column field="middleName" header="Презиме"></Column>
                      <Column field="lastName" header="Фамилия"></Column>
                      <Column field="egn" header="ЕГН"></Column>
                      <Column field="degreeName" header="ОКС"></Column>
                      <Column field="specialityName" header="Специалност"></Column>
                    </DataTable>
                  </>
                )}
              </div>
            </div>
            <div className="flex mt-2">
              <Button
                label="Напред"
                icon="pi pi-arrow-right"
                iconPos="right"
                type="button"
                onClick={() => {
                  getSpecialityFeeTrigger({
                    applicationId: application?.data.applicationId,
                    specialityId: formik.values.rankingApplication?.specialityId!,
                    specialityCode: formik.values.rankingApplication?.speciality?.specialityCode,
                    specialityModeId: formik.values.rankingApplication?.speciality?.specialityModeId,
                    specialityTypeId: formik.values.rankingApplication?.ranking.specialityTypeId!,
                    feeTypeId: formik.values.rankingApplication?.ranking.specialityTypeId!,
                  });
                  setActiveIndex(2);
                }}
              />
            </div>
          </AccordionTab>
          <AccordionTab header="Записване за студент">
            <div className="flex flex-column">
              <div className="flex">
                <div className="w-full">
                  {specialityChildren?.isLoading && <ProgressSpinner />}
                  {specialityChildren?.data && specialityChildren?.data?.data && (
                    <div className="flex flex-column gap-2">
                      <h3>Изберете специалност:</h3>
                      {specialityChildren.data.data.map((child: SpecialityResponse) => (
                        <div key={child.specialityId} className="flex align-items-center">
                          <RadioButton
                            inputId={`speciality-${child.specialityId}`}
                            name="selectedSpeciality"
                            value={child.specialityId}
                            onChange={(e) => {
                              formik.setFieldValue("selectedSpecialityId", e.value);
                            }}
                            checked={formik.values.selectedSpecialityId === child.specialityId}
                          />
                          <label htmlFor={`speciality-${child.specialityId}`} className="ml-2">
                            {child.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {specialityFee?.isLoading && <ProgressSpinner />}
                {specialityFee?.error && (
                  <Message severity="error" text="Не бяха намерени данни за таксата на избраната специалност." />
                )}
                {specialityFee?.data && specialityFee?.data?.data && (
                  <div className="w-full">
                    Семестриална такса за избраната специалност:{" "}
                    <strong>
                      {specialityFee?.data?.data?.feeAmount}{" "}
                      {specialityFee?.data?.data?.isForeignersFee ? "евро" : "лева"}
                    </strong>
                    <ul>
                      <li>{specialityFee?.data?.data?.specialityName}</li>
                      <li>{specialityFee?.data?.data?.specialityModeName}</li>
                      <li>{specialityFee?.data?.data?.specialityTypeName}</li>
                      <li>{specialityFee?.data?.data?.academicYearName}</li>
                      {specialityFee?.data?.data?.isForeignersFee && <li>за чужденци</li>}
                      {specialityFee?.data?.data?.isOutsideEu && <li>извън ЕС</li>}
                      {specialityFee?.data?.data?.isEnglishFee && <li>за обучение на английски език</li>}
                    </ul>
                  </div>
                )}
              </div>
              <div className="flex mt-2">
                <Button
                  label="Записване на студента"
                  icon="pi pi-save"
                  iconPos="right"
                  onClick={() => formik.handleSubmit()}
                />
              </div>
            </div>
          </AccordionTab>
        </Accordion>
      </FormikProvider>
    </div>
  );
};

export default Enrollment;
